import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { myWorks } from "../data/portfolio";
import css from "./ProjectDetail.module.css";
import { FiArrowLeft, FiExternalLink, FiCalendar, FiClock, FiUser, FiTag, FiPlay } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";

const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = myWorks.find((w) => w.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (project) {
      document.title = `${project.name} — KHEDID ABDELLAH | Portfolio`;
    }
    return () => {
      document.title = "KHEDID ABDELLAH | Full-Stack Web, Mobile & AI Developer";
    };
  }, [slug, project]);

  if (!project) {
    return (
      <div className={css.notFound}>
        <h1>Project Not Found</h1>
        <Link to="/" className={css.backBtn}>
          <FiArrowLeft /> Back to Portfolio
        </Link>
      </div>
    );
  }

  const currentIndex = myWorks.findIndex((w) => w.slug === slug);
  const prevProject = myWorks[currentIndex - 1];
  const nextProject = myWorks[currentIndex + 1];

  return (
    <div className={css.page}>
      {/* Navbar */}
      <nav className={css.nav}>
        <Link to="/#portfolio" className={css.navBack}>
          <FiArrowLeft />
          <span>Back to Portfolio</span>
        </Link>
      </nav>

      {/* Hero */}
      <header className={css.hero}>
        <div className={css.heroOverlay} />
        <img
          src={project.imgfull || project.img}
          alt={project.name}
          className={css.heroImage}
        />
        <div className={css.heroContent}>
          <span className={css.category}>{project.category}</span>
          <h1 className={css.title}>{project.name}</h1>
          <p className={css.shortInfo}>{project.shortInfo}</p>
          <div className={css.heroActions}>
            {project.details.link && (
              <a
                href={project.details.link}
                target="_blank"
                rel="noopener noreferrer"
                className={css.btnPrimary}
              >
                <FiExternalLink />
                <span>Live Demo</span>
              </a>
            )}
            {project.videoDemo && (
              <a
                href={project.videoDemo}
                target="_blank"
                rel="noopener noreferrer"
                className={css.btnVideo}
              >
                <FiPlay />
                <span>Watch Demo</span>
              </a>
            )}
            <a
              href={`https://wa.me/393522947771?text=Hi%20Abdellah,%20I%20saw%20your%20${encodeURIComponent(project.name)}%20project%20and%20I'd%20like%20to%20discuss%20something%20similar.`}
              target="_blank"
              rel="noopener noreferrer"
              className={css.btnWhatsApp}
            >
              <FaWhatsapp />
              <span>Discuss This Project</span>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={css.main}>
        <div className={css.container}>
          {/* Meta Info */}
          <div className={css.metaGrid}>
            <div className={css.metaItem}>
              <FiUser className={css.metaIcon} />
              <span className={css.metaLabel}>Client</span>
              <span className={css.metaValue}>{project.details.client}</span>
            </div>
            <div className={css.metaItem}>
              <FiTag className={css.metaIcon} />
              <span className={css.metaLabel}>Industry</span>
              <span className={css.metaValue}>{project.details.industry || "N/A"}</span>
            </div>
            <div className={css.metaItem}>
              <FiCalendar className={css.metaIcon} />
              <span className={css.metaLabel}>Year</span>
              <span className={css.metaValue}>{project.details.date}</span>
            </div>
            <div className={css.metaItem}>
              <FiClock className={css.metaIcon} />
              <span className={css.metaLabel}>Duration</span>
              <span className={css.metaValue}>{project.details.duration || "N/A"}</span>
            </div>
          </div>

          {/* Description */}
          <section className={css.section}>
            <h2 className={css.sectionTitle}>Project Overview</h2>
            <p className={css.description}>{project.fullDescription}</p>
          </section>

          {/* Tech Stack */}
          <section className={css.section}>
            <h2 className={css.sectionTitle}>Technology Stack</h2>
            <div className={css.techGrid}>
              {project.techStack.map((tech, i) => (
                <div key={i} className={css.techCard}>
                  <span className={css.techName}>{tech.name}</span>
                  <span className={css.techRole}>{tech.role}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Key Features */}
          <section className={css.section}>
            <h2 className={css.sectionTitle}>Key Features</h2>
            <div className={css.featuresGrid}>
              {project.keyFeatures.map((feature, i) => (
                <div key={i} className={css.featureItem}>
                  <span className={css.featureNumber}>{String(i + 1).padStart(2, "0")}</span>
                  <span className={css.featureText}>{feature}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Challenges */}
          <section className={css.section}>
            <h2 className={css.sectionTitle}>Challenges & Solutions</h2>
            <div className={css.challengesList}>
              {project.challenges.map((challenge, i) => (
                <div key={i} className={css.challengeItem}>
                  <span className={css.challengeDot} />
                  <p className={css.challengeText}>{challenge}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Results */}
          <section className={css.section}>
            <h2 className={css.sectionTitle}>Results & Impact</h2>
            <div className={css.resultsCard}>
              <p className={css.resultsText}>{project.results}</p>
            </div>
          </section>

          {/* Gallery */}
          {project.gallery && project.gallery.length > 1 && (
            <section className={css.section}>
              <h2 className={css.sectionTitle}>Project Gallery</h2>
              <div className={css.galleryGrid}>
                {project.gallery.map((img, i) => (
                  <div key={i} className={css.galleryItem}>
                    <img src={img} alt={`${project.name} screenshot ${i + 1}`} loading="lazy" />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* CTA */}
          <section className={css.ctaSection}>
            <div className={css.ctaCard}>
              <h3 className={css.ctaTitle}>Want something similar?</h3>
              <p className={css.ctaText}>
                I can build a custom solution tailored to your specific needs and budget.
              </p>
              <a
                href={`https://wa.me/393522947771?text=Hi%20Abdellah,%20I%20saw%20your%20${encodeURIComponent(project.name)}%20project%20and%20I'd%20like%20to%20discuss%20a%20similar%20project.`}
                target="_blank"
                rel="noopener noreferrer"
                className={css.ctaBtn}
              >
                <FaWhatsapp />
                <span>Start a Conversation</span>
              </a>
            </div>
          </section>
        </div>
      </main>

      {/* Prev / Next Navigation */}
      <footer className={css.footerNav}>
        <div className={css.footerContainer}>
          {prevProject ? (
            <button
              onClick={() => navigate(`/project/${prevProject.slug}`)}
              className={css.navProject}
            >
              <FiArrowLeft />
              <div>
                <span className={css.navLabel}>Previous</span>
                <span className={css.navName}>{prevProject.name}</span>
              </div>
            </button>
          ) : (
            <div />
          )}
          {nextProject ? (
            <button
              onClick={() => navigate(`/project/${nextProject.slug}`)}
              className={`${css.navProject} ${css.navProjectNext}`}
            >
              <div>
                <span className={css.navLabel}>Next</span>
                <span className={css.navName}>{nextProject.name}</span>
              </div>
              <FiArrowLeft style={{ transform: "rotate(180deg)" }} />
            </button>
          ) : (
            <div />
          )}
        </div>
      </footer>
    </div>
  );
};

export default ProjectDetail;
